import { create, type ArrayDelta, type Delta, type ObjectDelta } from 'jsondiffpatch';
import { formatPointer } from './jsonPointer';

// Delta shapes (jsondiffpatch exports its guards as types only).
const isAddedDelta = (d: Delta): boolean => Array.isArray(d) && d.length === 1;
const isModifiedDelta = (d: Delta): boolean => Array.isArray(d) && d.length === 2;
const isDeletedDelta = (d: Delta): boolean => Array.isArray(d) && d.length === 3 && d[2] === 0;
const isTextDiffDelta = (d: Delta): boolean => Array.isArray(d) && d.length === 3 && d[2] === 2;
const isMovedDelta = (d: Delta): boolean => Array.isArray(d) && d.length === 3 && d[2] === 3;
const isArrayDelta = (d: Delta): d is ArrayDelta =>
  typeof d === 'object' && d !== null && !Array.isArray(d) && (d as { _t?: unknown })._t === 'a';
const isObjectDelta = (d: Delta): d is ObjectDelta =>
  typeof d === 'object' && d !== null && !Array.isArray(d);

export type ChangeMark = 'added' | 'removed' | 'modified';

export interface DiffMarks {
  left: Map<string, ChangeMark>;
  right: Map<string, ChangeMark>;
  changed: boolean;
}

const differ = create({
  // Match blueprint resources by id and connections by their endpoints, so moves aren't reported as edits.
  objectHash: (item: object, index?: number) => {
    const value = item as Record<string, unknown>;
    if (typeof value.id === 'string') return value.id;
    if (typeof value.sourceId === 'string')
      return `${value.sourceId}->${String(value.targetId)}:${String(value.type)}`;
    return `$$index:${index ?? 0}`;
  },
  arrays: { detectMove: true },
});

export function diffJson(left: unknown, right: unknown): DiffMarks {
  const marks: DiffMarks = { left: new Map(), right: new Map(), changed: false };
  const delta = differ.diff(left, right);
  if (delta !== undefined) {
    marks.changed = true;
    walk(delta, [], [], left, right, marks);
  }
  return marks;
}

type Segments = (string | number)[];

function walk(
  delta: Delta,
  leftPath: Segments,
  rightPath: Segments,
  left: unknown,
  right: unknown,
  marks: DiffMarks,
) {
  const l = formatPointer(leftPath);
  const r = formatPointer(rightPath);
  if (isAddedDelta(delta)) {
    marks.right.set(r, 'added');
  } else if (isDeletedDelta(delta)) {
    marks.left.set(l, 'removed');
  } else if (isModifiedDelta(delta) || isTextDiffDelta(delta)) {
    marks.left.set(l, 'modified');
    marks.right.set(r, 'modified');
  } else if (isArrayDelta(delta)) {
    walkArray(delta, leftPath, rightPath, left, right, marks);
  } else if (isObjectDelta(delta)) {
    const leftObject = (left ?? {}) as Record<string, unknown>;
    const rightObject = (right ?? {}) as Record<string, unknown>;
    for (const [key, inner] of Object.entries(delta as ObjectDelta)) {
      walk(inner, [...leftPath, key], [...rightPath, key], leftObject[key], rightObject[key], marks);
    }
  }
}

function walkArray(
  delta: ArrayDelta,
  leftPath: Segments,
  rightPath: Segments,
  left: unknown,
  right: unknown,
  marks: DiffMarks,
) {
  const leftArray = Array.isArray(left) ? left : [];
  const rightArray = Array.isArray(right) ? right : [];
  const removedLeft = new Set<number>();
  const movedTo = new Map<number, number>(); // right index -> left index
  const addedRight = new Set<number>();
  const nested = new Map<number, Delta>();

  for (const [key, inner] of Object.entries(delta)) {
    if (key === '_t') continue;
    if (key.startsWith('_')) {
      const leftIndex = Number(key.slice(1));
      if (isMovedDelta(inner as Delta)) movedTo.set((inner as [unknown, number, 3])[1], leftIndex);
      else removedLeft.add(leftIndex);
      continue;
    }
    const rightIndex = Number(key);
    if (isAddedDelta(inner as Delta)) addedRight.add(rightIndex);
    else nested.set(rightIndex, inner as Delta);
  }

  // Pair the untouched items in order to find each right index's left index.
  const movedFrom = new Set(movedTo.values());
  const leftRemaining = leftArray.map((_, i) => i).filter((i) => !removedLeft.has(i) && !movedFrom.has(i));
  const rightRemaining = rightArray.map((_, i) => i).filter((i) => !addedRight.has(i) && !movedTo.has(i));
  const leftIndexOf = new Map<number, number>(movedTo);
  rightRemaining.forEach((rightIndex, n) => {
    if (leftRemaining[n] !== undefined) leftIndexOf.set(rightIndex, leftRemaining[n]);
  });

  for (const index of removedLeft) marks.left.set(formatPointer([...leftPath, index]), 'removed');
  for (const index of addedRight) marks.right.set(formatPointer([...rightPath, index]), 'added');
  for (const [rightIndex, leftIndex] of movedTo) {
    marks.left.set(formatPointer([...leftPath, leftIndex]), 'modified');
    marks.right.set(formatPointer([...rightPath, rightIndex]), 'modified');
  }
  for (const [rightIndex, inner] of nested) {
    const leftIndex = leftIndexOf.get(rightIndex) ?? rightIndex;
    walk(
      inner,
      [...leftPath, leftIndex],
      [...rightPath, rightIndex],
      leftArray[leftIndex],
      rightArray[rightIndex],
      marks,
    );
  }
}

export interface JsonLine {
  text: string;
  pointer: string;
  depth: number;
}

/** Pretty-printed JSON split into lines, each with the pointer of the value it belongs to. */
export function jsonLines(value: unknown): JsonLine[] {
  const lines: JsonLine[] = [];
  const emit = (v: unknown, path: Segments, depth: number, prefix: string, suffix: string) => {
    const pointer = formatPointer(path);
    if (Array.isArray(v) && v.length > 0) {
      lines.push({ text: `${prefix}[`, pointer, depth });
      v.forEach((item, i) => emit(item, [...path, i], depth + 1, '', i < v.length - 1 ? ',' : ''));
      lines.push({ text: `]${suffix}`, pointer, depth });
    } else if (v !== null && typeof v === 'object' && Object.keys(v).length > 0) {
      const entries = Object.entries(v as Record<string, unknown>).filter(([, item]) => item !== undefined);
      lines.push({ text: `${prefix}{`, pointer, depth });
      entries.forEach(([key, item], i) =>
        emit(item, [...path, key], depth + 1, `${JSON.stringify(key)}: `, i < entries.length - 1 ? ',' : ''),
      );
      lines.push({ text: `}${suffix}`, pointer, depth });
    } else {
      lines.push({ text: `${prefix}${JSON.stringify(v ?? null)}${suffix}`, pointer, depth });
    }
  };
  emit(value, [], 0, '', '');
  return lines;
}

/** The mark that applies to a line: its own pointer's or the nearest marked ancestor's. */
export function markForPointer(marks: Map<string, ChangeMark>, pointer: string): ChangeMark | undefined {
  let current = pointer;
  for (;;) {
    const mark = marks.get(current);
    if (mark) return mark;
    if (current === '') return undefined;
    current = current.slice(0, current.lastIndexOf('/'));
  }
}
